import breakPoints from 'helpers/Styles';
import Image from 'next/image';
import { RichText } from 'prismic-reactjs';
import PropTypes from 'prop-types';
import cn from 'classnames';

const ImageWithTwoColumns = ({ block, primaryFontColor, secondaryFontColor }) => {
  if (!block) return '';
  const { primary } = block;
  const title = primary?.block_title;
  const titleColour = primary?.block_title_colour
    ? primary.block_title_colour
    : primaryFontColor;
  const description = primary?.block_description;
  const descriptionColour = primary?.block_description_colour
    ? primary.block_description_colour
    : secondaryFontColor;
  const imageUrl = primary?.image?.url;
  const imageMobile = primary?.image_mobile?.url;
  const contentPosition = primary?.content_position;
  const videoSource = primary?.video_source;
  const contentPositionMobile = primary?.content_position_mobile;

  return (
    <div className='image-with-two-columns'>
      {videoSource ? (
        <div className='video'>
          <video loop muted autoPlay playsInline>
            <source src={videoSource} type='video/mp4' />
          </video>
        </div>
      ) : (
        <>
          <div className='image_desktop'>
            <Image
              draggable={false}
              layout='responsive'
              src={imageUrl}
              alt='Picture of block'
              width={1920}
              height={780}
            />
          </div>
          <div className='image_mobile'>
            <Image
              draggable={false}
              src={imageMobile}
              alt='Picture of block'
              width={primary?.image_mobile?.dimensions?.width || 780}
              height={primary?.image_mobile?.dimensions?.height || 780}
            />
          </div>
        </>
      )}

      <div
        className={cn({
          content: contentPosition === 'right',
          content_reverse: contentPosition !== 'right',
          content_mobile_static: contentPositionMobile === 'Below Image',
          content_mobile_absolute: contentPositionMobile === 'On Image',
        })}
      >
        {title && (
          <div
            className={`${
              contentPosition === 'right' ? 'title title-mobile-left' : 'title'
            }`}
          >
            {title}
          </div>
        )}
        {description && (
          <div
            className={`${
              contentPosition === 'right'
                ? 'description'
                : 'description_reverse'
            }`}
          >
            {RichText.render(description)}
          </div>
        )}
      </div>

      <style global jsx>
        {`
          p {
            margin: 0;
          }
        `}
      </style>

      <style jsx>
        {`
          .image-with-two-columns {
            position: relative;
            background-color: inherit;
          }
          .description_reverse {
            width: 60%;
            color: ${descriptionColour};
            font-family: 'Roboto-Medium';
            font-size: 30px;
            letter-spacing: 0;
            line-height: 37px;
            text-align: justify;
            padding: 20px;
          }
          .title {
            color: ${titleColour};
            font-family: 'big_noodle_titling';
            font-size: 101px;
            letter-spacing: 0;
            line-height: 101px;
            margin: 0;
            padding: 0px 20px;
          }
          .description {
            color: ${descriptionColour};
            font-family: 'Roboto-Medium';
            font-size: 30px;
            letter-spacing: 0;
            line-height: 37px;
            text-align: justify;
            padding: 20px;
          }
          .image_mobile {
            display: none;
          }
          .video {
            max-width: 100%;
            height: auto;
            max-height: 720px;
          }
          video {
            width: 100%;
            height: auto;
            object-fit: contain;
          }
          @media (min-width: ${breakPoints.medium}) {
            .content {
              position: absolute;
              left: 50%;
              top: 10%;
            }
            .content_reverse {
              margin: 0 auto;
              position: absolute;
              left: 5%;
              top: 10%;
            }
          }
          @media (min-width: 1500px) {
            .video {
              max-height: 920px;
            }
          }

          @media (min-width: 3000px) {
            .description,
            .description_reverse {
              font-size: 50px;
              line-height: 57px;
            }
          }

          @media (max-width: ${breakPoints.large}) {
            .title {
              font-size: 70px;
              letter-spacing: 0;
              line-height: 70px;
            }
            .description,
            .description_reverse {
              font-size: 21px;
              letter-spacing: 0;
              line-height: 26px;
            }
          }

          @media (max-width: ${breakPoints.medium}) {
            .title {
              font-size: 49px;
              line-height: 49px;
              text-align: center;
            }

            .description,
            .description_reverse {
              font-size: 15px;
              line-height: 19px;
              width: 100%;
            }
            .content_mobile_static {
              margin-top: 20px;
              position: static;
            }
            .content_mobile_absolute {
              position: absolute;
              top: 10%;
            }
          }
          @media (max-width: ${breakPoints.small}) {
            .title {
              font-size: 49px;
            }
            .description,
            .description_reverse {
              font-size: 15px;
              letter-spacing: 0;
              line-height: 19px;
              margin: 0 auto;
            }
            .image_desktop {
              display: none;
            }
            .image_mobile {
              display: block;
            }
          }
        `}
      </style>
    </div>
  );
};

ImageWithTwoColumns.propTypes = {
  block: PropTypes.shape({
    primary: PropTypes.shape({
      block_title: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      block_description: PropTypes.any,
      image: PropTypes.string,
      image_mobile: PropTypes.string,
      content_position: PropTypes.string,
      video_source: PropTypes.string,
      content_position_mobile: PropTypes.string,
    }),
  }),
};

ImageWithTwoColumns.defaultProps = {
  block: null,
};

export default ImageWithTwoColumns;
